/* global EpgEnabled, SeriesPromotionEnabled */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import TileGrid from 'components/TileGrid';
import Panel from 'components/Panel';
import ReactTimeout from 'react-timeout';
import InfiniteScroll from 'react-infinite-scroller';
import Loader from 'components/Loader';
import {
  seriesVideoProcessor,
  clipsVideoProcessor,
  seriesCollectionVideoProcessor,
  watchPlaylistVideoProcessor,
} from 'helpers/VideoProcessorHelper';
import { SHOW_DETAIL_RELATED_THUMBNAIL_IMAGE, renderImage } from 'helpers/ImageHelper';
import { helperGetApiHeaders, helperConvertApiRoute } from 'helpers/ApiHelper';
import { setHelmetTags } from 'helpers/HelmetHelper';
import { isBrowserIE } from 'helpers/GlobalHelper';
import { isClip, isEpisode, isLive } from 'helpers/ContentHelper';
import {
  BUILD_VERSION,
  callTealiumEvent,
  Events,
  getSegmentModelMetadata,
  handleAnalytics,
  SegmentActionLocations,
  setTealiumProfileId,
  setTealiumProfileStartSource,
  setTealiumSourceAuth,
  setTealiumSourceShow,
  TEALIUM_EVENT_PREFIX,
} from '@foxcorp/delta-web-analytics';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import { getTheme, webThemeIds } from 'helpers/ThemeHelper';
import { Header } from './components/Header';
import FirstTile from './components/FirstTile';
import UpsellBanner from './components/UpsellBanner';
import {
  getTabName,
  isSeriesCollection,
  TILE_TYPE_CONTINUE_WATCHING,
  TILE_TYPE_LATEST,
  TILE_TYPE_LIVE,
  TILE_TYPE_LATEST_PERSONALITY,
  TILE_TYPE_SERIES_PROMO,
} from '../../SeriesDetailHelpers';
import style from './styles/VideosTab.css';
import seriesCollectionTileStyle from './styles/seriesCollectionVideoTiles.css';

const videoTileStyle = getTheme(webThemeIds.SeriesDetail.VideosTab.VideoTile);

const FETCH_NEW_CONTENT_INTERVAL_MS = 1000 * 60 * 30;
const getProcessor = ({ videoType, seriesType, playlist }) => {
  if (playlist) return watchPlaylistVideoProcessor.bind(this, playlist);
  if (isSeriesCollection(seriesType)) return seriesCollectionVideoProcessor;

  switch (videoType) {
    case isEpisode(videoType):
      return seriesVideoProcessor;
    case isClip(videoType):
      return clipsVideoProcessor;
    default:
      return seriesVideoProcessor;
  }
};

class VideosTab extends Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.renderTab = this.renderTab.bind(this);
  }

  componentDidMount() {
    const { setInterval } = this.props;
    const refreshContent = () => {
      this.panel.refreshContent();
    };
    setInterval(refreshContent, FETCH_NEW_CONTENT_INTERVAL_MS);
    this.targetDiv?.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    this.targetDiv?.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = () => {
    const { fetching, fetchingPage, scrolling, hasMoreItems } = this.props;
    const isLoading = fetching || fetchingPage;
    const hasMore = hasMoreItems !== false && !scrolling;
    if (scrolling || isLoading || !this.panel || !hasMore) return;
    this.panel.fetchNextPage();
  };

  tealiumAuthStart = () => {
    const { account } = this.props;
    const action = 'authentication_start';
    const profileId = _.get(account, 'profileId');
    const authStartSource = 'series_detail_page';
    setTealiumSourceAuth(authStartSource);
    setTealiumProfileStartSource('detail page');
    const data = setTealiumProfileId(profileId, {
      action,
      auth_start_source: authStartSource,
      dcg_profile_id: profileId,
      platform_version: BUILD_VERSION,
      profile_start_source: 'detail page',
      ...getCommonTealiumProps(),
    });
    callTealiumEvent(`${TEALIUM_EVENT_PREFIX}${action}`, data);
    setTealiumSourceAuth(authStartSource);
  };

  toggleMvpdAuthOverlay = ({ e, text, show = true, buttonType }) => {
    e.stopPropagation();
    const { isAuthenticated, toggleMvpdAuthOverlay } = this.props;
    const options = {
      onAuthCheckComplete: this.redirectOnAuthCheckComplete,
      onOverlayClose: () => {
        toggleMvpdAuthOverlay(false);
      },
      showEmailForm: true,
      showLoaderAfterAuth: true,
      showUpsellProgressBar: true,
      upsellRedirectOnProviderClick: true,
    };
    if (show && !isAuthenticated) this.tealiumAuthStart();
    toggleMvpdAuthOverlay(show, options);
  };

  renderTab(tab) {
    const {
      account,
      authCheckComplete,
      authSuccess,
      breakpoint,
      continueWatching,
      fetching,
      fetchingPage,
      hasMoreItems,
      highlightFirstVideo,
      inline,
      isAuthenticated,
      isPersonality,
      latestEpisode,
      liveLatestEpisode,
      oldestEpisode,
      paginationItems,
      playlist,
      preauthorizedResources,
      renderFirstTile,
      saveLiveVideo,
      scrolling,
      seriesData,
      seriesType,
      setOverlay,
      tab: parentTab,
      tabName,
      tabSlug,
      tabs,
      videoModalProps,
    } = this.props;

    const { upsellPromoButton } = window.Language;
    const parentId = _.get(parentTab, 'id', null);
    const clips = _.get(tab, 'items.member', []);
    const videos = _.get(tab, 'member', clips);
    const headline = _.get(tab, 'headline');
    const newsHeadline = _.get(parentTab, 'headline');
    if (!videos || videos.length === 0) return null;
    const seo = _.get(tab, 'SEO');
    const videoType = _.get(videos, '0.videoType');
    const currentSeason = _.get(videos, '0.seasonNumber');
    const processor = getProcessor({ videoType, seriesType, playlist, breakpoint });
    const tileStyle = isSeriesCollection(seriesType) ? seriesCollectionTileStyle : videoTileStyle;
    const onLiveClick = video => {
      if (!EpgEnabled) saveLiveVideo(video);
    };
    const onClick = params => {
      const { originalItem, titleText: subtitle, uiElement } = params;
      let currentTabName = getTabName({ tab, seriesType });
      if (!currentTabName) currentTabName = getTabName({ tab: parentTab, seriesType });
      const title = _.get(originalItem, 'seriesName');
      handleAnalytics(
        {
          event: Events.PLAYLIST_BUTTON_ACTION,
          payload: {
            page_action_type: 'Watch Now',
            page_collection_title: currentSeason ? `Season ${currentSeason}` : '',
            page_item_title: title,
            page_item_subtitle: subtitle,
            page_action_location: SegmentActionLocations.DETAIL,
            page_ui_element: uiElement,
            ...getSegmentModelMetadata(),
          },
        },
        {
          event: Events.MENU_ITEM_SELECTED,
          payload: {
            page_category_title: currentTabName,
            page_item_title: title,
            page_item_subtitle: subtitle,
          },
        },
      );
      setTealiumSourceShow(true);
      if (isLive(_.get(originalItem, '@type'))) onLiveClick(originalItem);
    };

    const flattenedPaginationItems = _.flatten(paginationItems);
    const shouldDisplayUpsellCTA = false;

    let videosList = [...videos, ...flattenedPaginationItems];
    videosList = videosList.map(
      item =>
        item &&
        Object.assign(
          {
            isAuthenticated,
            preauthorizedResources,
          },
          item,
        ),
    );
    const isSingleEpisode = videosList.length === 1 && !isPersonality;
    const showcaseLatest = _.get(seriesData, 'showcaseOrder') === 'latest';

    let firstVideo = null;
    let firstTileType = null;
    if (isSingleEpisode && renderFirstTile) {
      firstVideo = _.get(videosList, '0');
      videosList = videosList.slice(1);
      firstTileType = TILE_TYPE_LATEST;
    } else if (!highlightFirstVideo || !renderFirstTile) {
      firstVideo = null;
    } else if (showcaseLatest) {
      firstVideo = _.get(videosList, '0');
      videosList = videosList.slice(1);
      firstTileType = TILE_TYPE_LATEST;
    } else if (isPersonality && latestEpisode) {
      firstVideo = latestEpisode;
      firstTileType = TILE_TYPE_LATEST_PERSONALITY;
    } else if (liveLatestEpisode) {
      firstVideo = liveLatestEpisode;
      firstTileType = TILE_TYPE_LIVE;
      const liveLatestEpisodeSeason = _.get(liveLatestEpisode, 'seasonNumber');
      const liveLatestEpisodeNumber = _.get(liveLatestEpisode, 'episodeNumber');
      _.remove(
        videosList,
        v =>
          v.seasonNumber === liveLatestEpisodeSeason && v.episodeNumber === liveLatestEpisodeNumber,
      );
    } else if (continueWatching) {
      firstVideo = continueWatching;
      firstTileType = TILE_TYPE_CONTINUE_WATCHING;
    } else if (SeriesPromotionEnabled && !_.isEmpty(oldestEpisode)) {
      firstVideo = oldestEpisode;
      firstTileType = TILE_TYPE_SERIES_PROMO;
    } else if (isEpisode(videoType)) {
      // Default to latest episode if not SeriesPromotionEnabled
      firstVideo = _.get(videosList, '0');
      videosList = videosList.slice(1);
      firstTileType = TILE_TYPE_LATEST;
    }

    const percentWatched = _.get(firstVideo, 'percentWatched');
    if (firstTileType === TILE_TYPE_LATEST && percentWatched) {
      firstTileType = TILE_TYPE_CONTINUE_WATCHING;
    }

    const seriesHeader =
      isPersonality && headline && videosList.length ? (
        <div className={style.seriesHeader}>{headline}</div>
      ) : null;
    const renderTileGrid = videosList.length > 0;
    const ctaText = upsellPromoButton;
    const tileGridProps = {
      context: 'seriesDetail',
      items: videosList,
      processor,
      gridType: 'grid-2-1',
      disableHoverStateActionButtons: true,
      renderImage: image =>
        renderImage({
          breakpoint,
          imageType: SHOW_DETAIL_RELATED_THUMBNAIL_IMAGE,
          ...image,
        }),
      tileDetailToggle: false,
      tileTheme: tileStyle,
      onTileClick: onClick,
      onTitleClick: onClick,
      displayUpsellCTA: shouldDisplayUpsellCTA,
    };
    const loader = fetching || fetchingPage ? <Loader /> : null;
    const hasMore = hasMoreItems !== false && !scrolling;
    const isIE = isBrowserIE();

    return (
      <div className={style.episodesWrapper} id={tabSlug || parentId} data-tab-id={parentId}>
        {setHelmetTags(seo)}
        {firstVideo && (
          <FirstTile
            account={account}
            authCheckComplete={authCheckComplete}
            authSuccess={authSuccess}
            breakpoint={breakpoint}
            isAuthenticated={isAuthenticated}
            isSingleEpisode={isSingleEpisode}
            onClick={onClick}
            onLiveClick={onLiveClick}
            seriesData={seriesData}
            setOverlay={setOverlay}
            tabs={tabs}
            type={firstTileType}
            video={{ ...firstVideo, isAuthenticated }}
            videoModalProps={videoModalProps}
            displayUpsellCTA={shouldDisplayUpsellCTA}
          />
        )}
        <Header
          videosList={videosList}
          currentSeason={currentSeason}
          videoType={videoType}
          seriesType={seriesType}
          style={style}
          tabName={tabName}
          newsHeadline={newsHeadline}
        />
        {renderFirstTile && (
          <UpsellBanner
            display={shouldDisplayUpsellCTA}
            inline={inline}
            toggleMvpdAuthOverlay={e =>
              this.toggleMvpdAuthOverlay({
                e,
                text: ctaText,
                buttonType: 'ad banner',
              })
            }
          />
        )}
        {isIE ? (
          <div
            className={style.scrollContainer}
            ref={div => {
              this.targetDiv = div;
            }}
          >
            {seriesHeader}
            {renderTileGrid && <TileGrid {...tileGridProps} />}
            {loader}
          </div>
        ) : (
          <InfiniteScroll pageStart={0} loadMore={this.handleScroll} hasMore={hasMore}>
            {seriesHeader}
            {renderTileGrid && <TileGrid {...tileGridProps} />}
            {loader}
          </InfiniteScroll>
        )}
      </div>
    );
  }

  render() {
    const {
      accessToken,
      authSuccess,
      deviceId,
      fetchingPage,
      isPersonality,
      preauthorizedResources,
      scrolling,
      tab,
      url,
    } = this.props;
    const panelId = isPersonality ? _.get(tab, 'id', url) : _.get(tab, '@id', url);
    return (
      <Panel
        accessToken={accessToken}
        deviceId={deviceId}
        renderer={this.renderTab}
        panelData={tab}
        panelUrl={url}
        headerGenerator={helperGetApiHeaders}
        routeGenerator={helperConvertApiRoute}
        pageType="seriesDetail"
        panelId={panelId}
        propsToWatch={{ authSuccess, fetchingPage, preauthorizedResources, scrolling }}
        data-test="web-series-detail"
        hasInfiniteScroll
        onRef={ref => {
          this.panel = ref;
        }}
      />
    );
  }
}

function mapStateToProps(state) {
  return {
    fetching: state.videosTab.fetching,
    nextPage: state.videosTab.nextPage,
    paginationItems: state.videosTab.paginationItems,
    hasMoreItems: state.videosTab.hasMoreItems,
    continueWatching: state.seriesDetail.continueWatching,
    oldestEpisode: state.seriesDetail.oldestEpisode,
    fetchingPage: state.panel.fetching,
  };
}

VideosTab.defaultProps = {
  accessToken: null,
  account: null,
  authCheckComplete: false,
  authSuccess: false,
  breakpoint: null,
  continueWatching: null,
  deviceId: null,
  fetching: false,
  fetchingPage: false,
  hasMoreItems: null,
  highlightFirstVideo: false,
  inline: {},
  isAuthenticated: false,
  isPersonality: false,
  latestEpisode: null,
  liveLatestEpisode: null,
  mvpdData: null,
  oldestEpisode: null,
  paginationItems: [],
  playlist: null,
  preauthorizedResources: [],
  renderFirstTile: true,
  scrolling: false,
  seriesData: null,
  seriesType: null,
  setOverlay: () => {},
  tab: null,
  tabName: null,
  tabSlug: null,
  tabs: null,
  toggleMvpdAuthOverlay: () => {},
  url: null,
  videoModalProps: null,
  videos: [],
};

VideosTab.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  authCheckComplete: PropTypes.bool,
  authSuccess: PropTypes.bool,
  breakpoint: PropTypes.string,
  continueWatching: PropTypes.shape({}),
  deviceId: PropTypes.string,
  fetching: PropTypes.bool,
  fetchingPage: PropTypes.bool,
  hasMoreItems: PropTypes.bool,
  highlightFirstVideo: PropTypes.bool,
  inline: PropTypes.shape({}),
  isAuthenticated: PropTypes.bool,
  isPersonality: PropTypes.bool,
  latestEpisode: PropTypes.shape({}),
  liveLatestEpisode: PropTypes.shape({}),
  mvpdData: PropTypes.shape({}),
  oldestEpisode: PropTypes.shape({}),
  paginationItems: PropTypes.arrayOf(PropTypes.shape({})),
  playlist: PropTypes.string,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  renderFirstTile: PropTypes.bool,
  saveLiveVideo: PropTypes.func.isRequired,
  scrolling: PropTypes.bool,
  seriesData: PropTypes.shape({}),
  seriesType: PropTypes.string,
  setInterval: PropTypes.func.isRequired,
  setOverlay: PropTypes.func,
  tab: PropTypes.shape({}),
  tabName: PropTypes.string,
  tabSlug: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.shape({})),
  toggleMvpdAuthOverlay: PropTypes.func,
  url: PropTypes.string,
  videoModalProps: PropTypes.shape({}),
  videos: PropTypes.arrayOf(PropTypes.shape({})),
};

export default ReactTimeout(connect(mapStateToProps, {})(VideosTab));
